import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '../services/authService';
import { verify_otp } from '../services/loginServices';


const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  loading: true,
  isAuthenticated: false,
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      console.log("Hello Dispatch",action.payload);
      localStorage.setItem('access_token', action.payload.access_token);
      localStorage.setItem('refresh_token', action.payload.refresh_token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.access_token,
        refreshToken: action.payload.refresh_token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGOUT':
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return {
        ...state,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        loading: false,
      };
    case 'TOKEN_REFRESH':
      localStorage.setItem('access_token', action.payload.access_token);
      localStorage.setItem('refresh_token', action.payload.refresh_token);
      return {
        ...state,
        token: action.payload.access_token,
        refreshToken: action.payload.refresh_token,
        user: action.payload.user,
        isAuthenticated: true,
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refreshTokenValue = localStorage.getItem('refresh_token');

      console.log('Initializing auth with token:', token ? 'exists' : 'none');

      if (token) {
        try {
          console.log('Attempting to get current user with existing token');
          const user = await authService.getCurrentUser();
          console.log('Successfully retrieved user:', user);
          dispatch({ type: 'SET_USER', payload: user });
        } catch (error) {
          console.error('Failed to get current user:', error);

          // Try to refresh the token if refresh token exists
          if (refreshTokenValue) {
            try {
              console.log('Attempting to refresh token');
              const response = await authService.refreshToken(refreshTokenValue);
              console.log('Token refreshed successfully');
              dispatch({ type: 'TOKEN_REFRESH', payload: response });

              // Wait a moment for state to update
              await new Promise(resolve => setTimeout(resolve, 100));

              // Retry getting user with new token
              console.log('Retrying to get user with new token');
              const user = await authService.getCurrentUser();
              console.log('Successfully retrieved user after token refresh');
              dispatch({ type: 'SET_USER', payload: user });
            } catch (refreshError) {
              console.error('Failed to refresh token:', refreshError);
              dispatch({ type: 'LOGOUT' });
            }
          } else {
            console.log('No refresh token available, logging out');
            dispatch({ type: 'LOGOUT' });
          }
        }
      } else {
        console.log('No token found, setting loading to false');
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authService.login(credentials);
      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
      return { success: true };
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  };


  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token');
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshTokenValue);
      dispatch({ type: 'TOKEN_REFRESH', payload: response });
      return response.access_token;
    } catch (error) {
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  // Navigation guard to clear tokens when going to public pages
  const clearTokensForPublicPages = () => {
    const currentPath = window.location.pathname;
    const isPublicPage = currentPath === '/' ||
      currentPath.startsWith('/delivery') ||
      currentPath.startsWith('/takeaway') ||
      currentPath.startsWith('/catering') ||
      currentPath.startsWith('/table-reservations') ||
      currentPath.startsWith('/table-order');

    if (isPublicPage && (state.token || state.refreshToken)) {
      console.log('Detected navigation to public page - clearing authentication tokens');
      dispatch({ type: 'LOGOUT' });
      return true; // Tokens were cleared
    }
    return false; // No tokens cleared
  };

  const value = {
    ...state,
    login,
    logout,
    refreshToken,
    clearTokensForPublicPages,

  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

